import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Grid } from '@mui/material';
import PublicService from 'services/PublicService';
import ActivityCard from 'components/ActivityCard';
import AddBookmark from 'components/AddBookmark';
import { Container, CarouselSection, CarouselTitle, CarouselWrapper } from './styles';
import { useLoading } from 'hooks/useLoading';
import { IBusiness } from 'interfaces/IBusinesses';
import IExperience from 'interfaces/IExperience';

interface IProps {
  selectedExperience?: string | null;
  experiences?: IExperience[];
}

interface ExperienceCarouselProps {
  title: string;
  activities: IBusiness[];
}

const ExperienceCarousel = ({ title, activities }: ExperienceCarouselProps): JSX.Element => {
  return (
    <CarouselSection>
      {/* <CarouselTitle>{title}</CarouselTitle> */}
      <CarouselWrapper>
        <Grid container spacing={3}>
          {activities.map((activity, index) => (
            <Grid key={activity.id ?? index} xs={12} sm={6} md={4} lg={3} item>
              <ActivityCard
                image={Array.isArray(activity.image) ? activity.image[0] : activity.image}
                category={activity.businessType}
                title={activity.title ? activity.title : activity.tradeName}
                description={activity.description}
                link={`/activities/${activity.id}`}
                action={<AddBookmark type="business" id={activity.id} />}
              />
            </Grid>
          ))}
        </Grid>
      </CarouselWrapper>
    </CarouselSection>
  );
};

const ActivitiesList = (props: IProps): JSX.Element => {
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const experienceFromUrl = queryParams.get('experience');
  const activeExperience = props.selectedExperience !== undefined ? props.selectedExperience : experienceFromUrl;

  const [allActivities, setAllActivities] = useState<IBusiness[]>([]);
  const { openLoading, closeLoading } = useLoading();

  useEffect(() => {
    const getActivities = async () => {
      try {
        openLoading();
        const service = new PublicService();
        const data = await service.getAllActivities();
        setAllActivities(data);
      } catch (error) {
        console.error('Erro ao buscar atividades:', error);
      } finally {
        closeLoading();
      }
    };

    getActivities();
  }, []);

  // Agrupa as atividades por vetor de experiência
  const groupedByExperience = useMemo(() => {
    const groups: Record<string, IBusiness[]> = {};

    allActivities.forEach(activity => {
      const exps = activity.experiences ?? [];
      exps.forEach(exp => {
        const key = exp.trim();
        if (!groups[key]) groups[key] = [];
        groups[key].push(activity);
      });
    });

    return groups;
  }, [allActivities]);

  // Ordem dos vetores respeitando a ordem vinda das experiências do servidor
  const orderedExperienceTitles = useMemo(() => {
    if (props.experiences && props.experiences.length > 0) {
      return props.experiences.map(e => e.title).filter(title => !!groupedByExperience[title]);
    }
    return Object.keys(groupedByExperience);
  }, [props.experiences, groupedByExperience]);

  // Quando há um vetor selecionado, exibe apenas o carrossel desse vetor
  if (activeExperience) {
    const normalizedActive = activeExperience.trim().toLowerCase();
    const matchingKey = Object.keys(groupedByExperience).find(key => key.toLowerCase() === normalizedActive);
    const activities = matchingKey ? groupedByExperience[matchingKey] : [];

    return (
      <Container>
        <Box sx={{ flexGrow: 1 }}>
          <ExperienceCarousel title={activeExperience} activities={activities} />
        </Box>
      </Container>
    );
  }

  // Quando nenhum vetor está selecionado, exibe todos com carroseis individuais
  return (
    <Container>
      <Box sx={{ flexGrow: 1 }}>
        {orderedExperienceTitles.map(expTitle => (
          <ExperienceCarousel key={expTitle} title={expTitle} activities={groupedByExperience[expTitle]} />
        ))}
      </Box>
    </Container>
  );
};

export default ActivitiesList;
